import { useEffect, useRef } from "react";

import { useSources, usePageProducts, useOptionalActivePageId } from "../../data";
import { getPendingLicenceAgreements, unresolvedPendingSourceIds } from "./pendingAgreements";
import { useUserExchangeUsage } from "../../data/user";
import { useUserId } from "../../context/auth/hooks";

export function useLicenceAgreement() {
  const userId = useUserId();
  const exchangeUsage = useUserExchangeUsage(userId);
  const products = usePageProducts(useOptionalActivePageId());
  const { data: sources, isLoading: sourcesLoading, refetch: refetchSources } = useSources();

  const usage = exchangeUsage.data?.exchange_usage_last;

  // A grant can reach the usage subscription before the permission change has refreshed the source
  // list, and an agreement cannot be identified without its source name. Refetching once per unseen
  // id covers that gap without spinning when a source stays invisible.
  const unresolvedIds = unresolvedPendingSourceIds(usage, sources).join(",");
  const refetchedFor = useRef("");

  useEffect(() => {
    if (!unresolvedIds || unresolvedIds === refetchedFor.current) return;
    refetchedFor.current = unresolvedIds;
    void refetchSources();
  }, [unresolvedIds, refetchSources]);

  const isInitialLoading =
    exchangeUsage.isLoading || (sourcesLoading && !sources) || (products.isLoading && !products.data);

  const pendingAgreements = getPendingLicenceAgreements(usage, sources);

  if (pendingAgreements.length === 0) {
    return {
      licenceAgreement: null,
      sourceId: null,
      isInitialLoading,
    };
  }

  const { name: licenceAgreement, sourceId } = pendingAgreements[0];

  return { licenceAgreement, sourceId, isInitialLoading };
}
